// Halftone cloud shader ported from new-crosby-ai, without the React renderer.
export const shaderDefaults = {
    speed: 3, textureScale: 2.4, detail: 2.5, warp: 0.25, grain: 1,
    pixelSize: 1, contrast: 0.95, rotation: -5, seed: 93, clearCenter: 0.6,
};

export const vertexSource = `
  attribute vec2 a_position;
  void main() { gl_Position = vec4(a_position, 0.0, 1.0); }
`;

export const fragmentSource = `
  precision highp float;
  uniform vec2 u_resolution;
  uniform vec3 u_tint, u_colorA, u_colorB, u_colorC;
  uniform float u_colorStrength, u_colorSpread, u_colorAngle;
  uniform float u_time, u_scale, u_detail, u_warp, u_grain, u_pixelSize;
  uniform float u_contrast, u_rotation, u_seed, u_clearCenter;

  float hash21(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * .1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }
  vec2 hash22(vec2 p) { return vec2(hash21(p), hash21(p + 71.7)); }
  float noise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash21(i), hash21(i + vec2(1,0)), u.x),
      mix(hash21(i + vec2(0,1)), hash21(i + vec2(1,1)), u.x), u.y);
  }
  float fbm(vec2 p) {
    float value = 0.0, amplitude = .5;
    for (int i = 0; i < 4; i++) {
      value += noise(p) * amplitude;
      p = mat2(.8, -.6, .6, .8) * p * 2.03 + 7.13;
      amplitude *= .5;
    }
    return value;
  }
  mat2 rotate(float a) { return mat2(cos(a), -sin(a), sin(a), cos(a)); }
  vec2 voronoi(vec2 p, float time) {
    vec2 cell = floor(p);
    vec2 result = vec2(10.0);
    for (int y = -1; y <= 1; y++) for (int x = -1; x <= 1; x++) {
      vec2 id = cell + vec2(float(x), float(y));
      vec2 point = .5 + .4 * sin(time + 6.283 * hash22(id));
      float d = length(id + point - p);
      if (d < result.x) result = vec2(d, result.x);
      else result.y = min(result.y, d);
    }
    return result;
  }

    float textureField(vec2 p) {
      float spacing = max(3.0, 12.0 / u_detail) * u_pixelSize;
      vec2 cell = floor(gl_FragCoord.xy / spacing);
      vec2 center = (cell + .5) * spacing;
      vec2 q = (center - u_resolution * .5) / u_resolution.y * 3.0 * u_scale;
      q = rotate(u_rotation) * q + u_seed * .17;
      q += vec2(fbm(q + u_time * .035), fbm(q + 9.0 - u_time * .03)) * u_warp;
      float cloud = smoothstep(.2, .8, fbm(q * 1.3 + vec2(u_time * .04, 0.0)));
      float radius = .035 + cloud * .46;
      float distance = length(fract(gl_FragCoord.xy / spacing) - .5);
      return (1.0 - smoothstep(radius - .04, radius + .04, distance)) * .9;
    }
  void main() {
    vec2 pixel = floor(gl_FragCoord.xy / u_pixelSize) * u_pixelSize + u_pixelSize * .5;
    vec2 p = (pixel - u_resolution * .5) / u_resolution.y * 3.0 * u_scale;
    p = rotate(u_rotation) * p + u_seed * .17;
    float field = clamp(textureField(p), 0.0, 1.0);
    field = clamp((field - .35) * u_contrast + .35, 0.0, 1.0);
    float grain = hash21(floor(pixel));
    field = mix(field, smoothstep(grain - .1, grain + .1, field), u_grain);
    vec2 center = (gl_FragCoord.xy / u_resolution - .5) * vec2(1.3, 1.8);
    float quiet = 1.0 - u_clearCenter * (1.0 - smoothstep(.08, .65, length(center)));
    vec2 colorPoint = rotate(u_colorAngle) * (gl_FragCoord.xy / u_resolution - .5);
    float drift = fbm(p * .5 + vec2(u_time * .025, 0.0));
    float blend = clamp(.5 + colorPoint.x * u_colorSpread + (drift - .5) * .6, 0.0, 1.0);
    vec3 mixed = mix(u_colorA, u_colorB, smoothstep(.0, .5, blend));
    mixed = mix(mixed, u_colorC, smoothstep(.5, 1.0, blend));
    mixed = min(mixed * 1.45, vec3(1.0));
    gl_FragColor = vec4(mix(u_tint, mixed, u_colorStrength), field * quiet);
  }
`;
